import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  Paper,
  Box,
  useTheme,
  TextField,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { tokens } from "../../../theme";
import Header from "../../Dashboard/Header";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

export default function ExcelCompare() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState({
    from: "",
    to: "",
  });

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [headerNumber, setHeaderNumber] = useState("");

  //======== Doc read functions ========

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });

      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];

      // Get raw data as array of arrays
      const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

      if (!data.length) return;

      // Use user-provided header row number
      const headerRowIndex = headerNumber ? parseInt(headerNumber, 10) - 1 : 0;
      const headers = data[headerRowIndex] || [];

      // Filter out completely empty headers
      const validHeaders = headers
        .map((h, idx) => (h ? { header: h, idx } : null))
        .filter(Boolean);

      // Create DataGrid columns
      const cols = validHeaders.map(({ header, idx }) => ({
        field: header,
        headerName: header,
        flex: 1,
        minWidth: 150,
      }));

      const body = data.slice(headerRowIndex + 1);

      // filter out completely empty rows
      const filteredBody = body.filter((row) =>
        row.some((cell) => cell !== null && cell !== undefined && cell !== "")
      );

      const formattedRows = filteredBody.map((row, index) => {
        const rowObj = {};
        headers.forEach((header, colIdx) => {
          rowObj[header || `col${colIdx}`] = row[colIdx];
        });
        return { id: index + 1, ...rowObj };
      });

      setColumns(cols);
      setRows(formattedRows);
      // console.log("all data",data);
    };
    reader.readAsBinaryString(file);
  };

  // ============================================================

  const handleSave = () => {
    if (fileName && dateRange && headerNumber) {
      localStorage.setItem("columns", JSON.stringify(columns));
      localStorage.setItem("rows", JSON.stringify(rows));
      localStorage.setItem("dateRange", JSON.stringify(dateRange));
      // localStorage.setItem("fileName", fileName);
      navigate("/Infinite/BankReadDoc");
    } else {
      // alert("Please fill all details...");
      
        Swal.fire({
          title: "Warning",
          text: "Please fill all Details.",
          icon: "warning",
          confirmButtonText: "OK",
          confirmButtonColor: "#f0ad4e",
          width: 300,
          padding: "1em",
          customClass: { popup: "small-alert" },
        });
       
      
    }
  };
  

  return (
    <Box mt="80px" ml="20px" mr="20px">
      <Header title="Bank Reconciliation" subtitle="Basic Bank Details" />
      <Box display="flex" justifyContent="center" alignItems="center">
        <Paper
          elevation={4}
          style={{
            padding: 30,
            minHeight: "400px",
            width: "600px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={2}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={dateRange.from}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, from: e.target.value }))
              }
              fullWidth
            />
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={dateRange.to}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, to: e.target.value }))
              }
              fullWidth
            />
          </Stack>

          {/* Middle - Excel Header Number */}
          <TextField
            label="Enter Excel Header row Number"
            type="number"
            value={headerNumber}
            onChange={(e) => setHeaderNumber(e.target.value)}
            fullWidth
            sx={{ mt: 3 }}
          />

          {/* Bottom - File Upload + Save */}
          <Stack direction="row" spacing={2} alignItems="center" mt={3}>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
              sx={{
                backgroundColor: "#1976d2", //"#d9534f",
                borderRadius: "50px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 500,
                padding: "10px 24px",
                "&:hover": {
                  backgroundColor: "#115293", //"#c9302c",
                },
              }}
            >
              Choose File
              <input
                type="file"
                hidden
                onChange={handleFileUpload}
                accept=".xlsx,.xls"
              />
            </Button>
            <Typography>{fileName}</Typography>
          </Stack>

          <Box mt={4} textAlign="right">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.primary[100],
                "&:hover": { backgroundColor: colors.blueAccent[700] },
              }}
            >
              Submit
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
